import { useCallback } from 'react';
import { pick } from 'ramda';
import { useDispatch } from 'react-redux';
import { useAuthSelector, useUserSelector } from 'js/store/hooks';
import { userSetAttributesAction } from 'js/hooks/user/store/actions';
import { publicEventsAPIService } from 'js/services/publicEventsAPI.service';

import * as pas from '../pas';
import * as helpers from '../helpers';
import * as actions from '../store/actions';
import { useGetLoginMode } from './useGetLoginMode';
import { useLoginRequest } from './useLoginRequest';
import { useValidateSession } from './useValidateSession';
import { useBeforeLoginAction } from './useBeforeLoginAction';

export const useInitialLoginAndSessionValidation = () => {
    const dispatch = useDispatch();
    const request = useLoginRequest();
    const getLoginMode = useGetLoginMode();
    const validateSession = useValidateSession();
    const beforeLoginAction = useBeforeLoginAction();
    const { role, isLoggedIn } = useUserSelector(pick(['role', 'isLoggedIn']));
    const { isLoginInProcess, isLogoutInProcess } = useAuthSelector(pick(['isLoginInProcess', 'isLogoutInProcess']));

    return useCallback(async (inputUserName, password, rememberMeLogin, afterRegisterLogin, loginMode, fitToPlay) => {
        if (isLoggedIn) {
            return;
        }

        if (role === 'admin') {
            return Promise.reject({ errorMessage: 'Admin can\'t login to portal' });
        }

        if (isLoginInProcess || isLogoutInProcess || !request) {
            return Promise.reject();
        }

        dispatch(actions.setIsLoginInProcess(true));
        beforeLoginAction();

        try {
            const { languageCode } = request;
            const userNameLoginMode = getLoginMode(inputUserName);
            const initialCredentials = {
                userName: inputUserName,
                password,
                loginMode: loginMode || userNameLoginMode,
                languageCode,
                rememberMeLogin
            };

            const pasLoginResponse = await pas.login(initialCredentials);

            const {
                userId,
                loginName,
                userName,
                sessionToken: initialSessionToken,
                needsTokenRequest
            } = helpers.parsePasLoginResponse(pasLoginResponse);
            const credentials = { ...initialCredentials, userName, loginName, userId };

            dispatch(userSetAttributesAction({
                loginName,
                userId,
                userName,
                password,
                rememberMeLogin
            }));

            const validationSessionToken = await validateSession(pasLoginResponse, {
                afterRegisterLogin,
                fitToPlay,
                isAfterLoginWithCredentials: true,
                isLoginToNativeAppEnabled: true
            });

            if (needsTokenRequest) {
                const tokenResponse = await pas.getTempToken();

                return { ...credentials, sessionToken: tokenResponse.data.sessionToken.sessionToken };
            }

            const sessionToken = validationSessionToken || initialSessionToken;

            if (!sessionToken) {
                dispatch(actions.setIsLoginInProcess(false));
                publicEventsAPIService.trigger('auth:credentials:login:failed', pasLoginResponse);
                publicEventsAPIService.trigger('auth:generic_login:error', pasLoginResponse);

                return Promise.reject(pasLoginResponse);
            }

            return { ...credentials, sessionToken };
        } catch (error) {
            dispatch(actions.setIsLoginInProcess(false));
            publicEventsAPIService.trigger('auth:credentials:login:failed', error);
            publicEventsAPIService.trigger('auth:generic_login:error', error);

            throw error;
        }
    }, [
        isLoggedIn, role, request, isLoginInProcess, isLogoutInProcess, getLoginMode,
        dispatch, beforeLoginAction, validateSession
    ]);
};
